import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../api";
import { useLang } from "../context/LanguageContext";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | failed | expired
  const [plan, setPlan] = useState(null);
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPlan({ plan_id: data.plan_id, cycle: data.cycle });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        if (attempts++ < 20) setTimeout(poll, 2000);
        else setStatus("failed");
      } catch (e) {
        if (attempts++ < 20) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFFDF9] bg-grain grid place-items-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm">
        <Link to="/" className="inline-flex items-center gap-2 mb-8" data-testid="brand-link">
          <div className="w-9 h-9 rounded-lg bg-[#0A2540] grid place-items-center text-white font-bold font-display">R</div>
          <span className="font-display font-semibold text-lg">ReviewFlow</span>
        </Link>
        {status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#2B8C9F] animate-spin" />
            <h1 className="mt-6 font-display text-2xl font-bold text-[#0A2540]">Confermiamo il pagamento…</h1>
            <p className="mt-2 text-slate-600 text-sm">Ci vogliono pochi secondi.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#10B981]" data-testid="payment-success-icon" />
            <h1 className="mt-6 font-display text-3xl font-bold text-[#0A2540]">Benvenuto a bordo!</h1>
            <p className="mt-3 text-slate-600">
              Il tuo piano <span className="font-semibold capitalize">{plan?.plan_id}</span> è attivo con <strong>1 giorno di prova gratuita</strong>. Il primo addebito partirà solo dopo la fine del trial.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-8 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white h-12 px-8 gap-2" data-testid="go-dashboard-btn">
              {t("nav_dashboard")} <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <h1 className="mt-4 font-display text-2xl font-bold text-[#0A2540]">Pagamento non completato</h1>
            <p className="mt-2 text-slate-600 text-sm">Riprova o torna alla home.</p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button onClick={() => navigate("/")} variant="outline" className="rounded-full">Home</Button>
              <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white">Dashboard</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
